import { useEffect } from 'react';
import type { FmctResponse } from 'src/apis/contract-management/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';

type HookConfig = {
  url: string;
};

type ExecuteProps = {
  url: string;
};

export function useFmct({ url }: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<FmctResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [url]);

  return request;
}

export type FmctRequest = ReturnType<typeof useFmct>;

type OptionalHookConfig = {
  url?: string;
};

export function useOptionalFmct({ url }: OptionalHookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<FmctResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // workaround for https://prisma.atlassian.net/browse/TAG-8049
        if (isServerError(error, 403)) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!url) return;
    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [url]);

  if (!url) return;
  return request;
}

export type OptionalFmctRequest = ReturnType<typeof useOptionalFmct>;
